import React from "react";
import { FormControlLabel, Tooltip, Grid, useTheme } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import PropTypes from "prop-types";

/**
 * FormControlLabel with an optional tooltip icon.
 *
 * @param {string} props.label - Label text
 * @param {React.ReactNode} props.control - Control element (Switch, Checkbox, etc.)
 * @param {string} [props.tooltipText] - Tooltip text
 * @param {Object} [props.gridProps] - Grid container props
 * @param {Object} [props.formControlLabelProps] - FormControlLabel props
 * @param {Object} [props.iconSx] - Tooltip icon styles
 * @param {Object} [props.tooltipProps] - Tooltip component props
 */
const FormControlWithTooltip = ({
  label,
  control,
  tooltipText,
  gridProps = {},
  formControlLabelProps = {},
  iconSx = {},
  tooltipProps = {},
}) => {
  const theme = useTheme();

  const defaultTooltipStyles = {
    tooltip: {
      sx: {
        backgroundColor: "#282828 !important",
        borderRadius: "4px",
        padding: "4px 8px",
        color: theme.palette.primary.contrastText,
        whiteSpace: "nowrap",
        maxWidth: "none",
        ...tooltipProps?.tooltip?.sx,
      },
    },
  };

  const defaultIconSx = {
    color: theme.palette.lightText.main,
    ...iconSx,
  };

  const tooltipIcon = tooltipText ? (
    <Tooltip
      title={tooltipText}
      componentsProps={defaultTooltipStyles}
      {...tooltipProps}
    >
      <InfoOutlined sx={defaultIconSx} />
    </Tooltip>
  ) : null;

  return (
    <Grid container alignSelf="center" alignItems="center" {...gridProps}>
      <FormControlLabel
        label={label}
        control={control}
        {...formControlLabelProps}
      />
      {tooltipIcon}
    </Grid>
  );
};

FormControlWithTooltip.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  control: PropTypes.node.isRequired,
  tooltipText: PropTypes.string,
  tooltipPosition: PropTypes.oneOf(["before", "after"]),
  gridProps: PropTypes.object,
  formControlLabelProps: PropTypes.object,
  iconSx: PropTypes.object,
  tooltipProps: PropTypes.object,
};

export default FormControlWithTooltip;
